import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ltrIsolate } from 'shared/lib/bidi';
import { cn } from 'shared/lib/utils';

interface ProgressSummaryCardProps {
  currentStageTitle: string;
  completedCount: number;
  totalCount: number;
  progressPercent: number;
  className?: string;
}

/**
 * Condenses the current stage, completed count and progress percentage into a
 * single card. Used on narrow screens where the three stacked KPI tiles push
 * the onboarding steps below the fold.
 */
const ProgressSummaryCard: React.FC<ProgressSummaryCardProps> = ({
  currentStageTitle,
  completedCount,
  totalCount,
  progressPercent,
  className,
}) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  return (
    <div
      className={cn(
        'rounded-2xl border border-gray-100 bg-white p-4 shadow-sm',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#C00035]/10 text-[#C00035]">
              <Clock className="h-4 w-4" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {t('partnerPortal.kpi.currentStage')}
            </span>
          </div>
          <p className="mt-2 text-base font-bold leading-snug text-gray-900">
            {currentStageTitle}
          </p>
        </div>
        <span className="shrink-0 text-2xl font-bold tabular-nums text-[#C00035]">
          {ltrIsolate(`${progressPercent}%`)}
        </span>
      </div>

      <div
        role="progressbar"
        aria-label={t('partnerPortal.kpi.progress') ?? undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progressPercent}
        className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100"
      >
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-[#C00035] to-[#99002A]"
          initial={reduceMotion ? false : { width: 0 }}
          animate={{ width: `${progressPercent}%` }}
          transition={{ duration: reduceMotion ? 0 : 1, ease: 'easeOut' }}
        />
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-3 text-xs">
        <span className="font-medium text-gray-500">
          {t('partnerPortal.kpi.completed')}
        </span>
        <span className="font-bold tabular-nums text-gray-900">
          {ltrIsolate(`${completedCount} / ${totalCount}`)}
        </span>
      </div>
    </div>
  );
};

export default ProgressSummaryCard;
